import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PaymentIcon from "@mui/icons-material/Payment";

import useWorkspaceNavigation from "../hooks/useWorkspaceNavigation";
import {
  getInvoiceById,
  resetInvoiceServerStatus,
} from "../redux/actions/invoiceActions";

import InvoiceDetailsTab from "../components/invoices/InvoiceDetailsTab";
import InvoicePdfTab from "../components/invoices/InvoicePdfTab";
import PayInvoiceModal from "../components/invoices/PayInvoiceModal";
import AppCircularProgress from "../components/common/AppCircularProgress";
import AppButton from "../components/buttons/AppButton";
import StatusChip from "../components/common/StatusChip";
import PageError from "../components/common/PageError";

const InvoicePage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { closeCurrentAndNavigate } = useWorkspaceNavigation();

  const { currentInvoice, loadingInvoice, errorLoadingInvoice, serverStatus } =
    useSelector((state) => state.invoice);

  const [activeTab, setActiveTab] = useState(0);
  const [invoiceId, setInvoiceId] = useState(null);
  const [isPayOpen, setIsPayOpen] = useState(false);

  useEffect(() => {
    const uuidRegex =
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
    const match = location.pathname.match(uuidRegex);
    if (match && match[0]) setInvoiceId(match[0]);
  }, [location.pathname]);

  useEffect(() => {
    if (invoiceId) dispatch(getInvoiceById(invoiceId));
  }, [dispatch, invoiceId]);

  useEffect(() => {
    if (serverStatus?.operation === "pay" && serverStatus?.status === 200) {
      setIsPayOpen(false);
      dispatch(resetInvoiceServerStatus());
    }
  }, [serverStatus, dispatch]);

  if (loadingInvoice) return <AppCircularProgress />;
  if (errorLoadingInvoice)
    return (
      <PageError
        error={errorLoadingInvoice}
        onClick={dispatch(getInvoiceById(invoiceId))}
      />
    );
  if (!currentInvoice) return null;

  const balance = currentInvoice.total - currentInvoice.amountPaid;
  const isFullyPaid = balance <= 0 || currentInvoice.status === "PAID";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "background.default",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          pt: 3,
          pb: 1,
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton
              onClick={() => closeCurrentAndNavigate("/invoices")}
              color="inherit"
              size="small"
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" fontWeight="bold">
              Invoice #{currentInvoice.invoiceNumber}
            </Typography>
            {!isMobile && <StatusChip status={currentInvoice.status} />}
          </Stack>

          {/* Record Payment Button */}
          {!isFullyPaid && (
            <AppButton
              variant="contained"
              color="secondary"
              startIcon={<PaymentIcon />}
              onClick={() => setIsPayOpen(true)}
            >
              Record Payment
            </AppButton>
          )}
        </Stack>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ ml: { xs: 0, sm: 6 }, mb: 2 }}
        >
          Issued: {new Date(currentInvoice.issueDate).toLocaleDateString()} •
          Due: {new Date(currentInvoice.dueDate).toLocaleDateString()}
        </Typography>

        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          indicatorColor="primary"
          textColor="primary"
          variant={isMobile ? "fullWidth" : "standard"}
        >
          <Tab label="Invoice Details" />
          <Tab label="PDF Document" />
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", p: { xs: 1.5, sm: 3 } }}>
        {activeTab === 0 && <InvoiceDetailsTab invoice={currentInvoice} />}
        {activeTab === 1 && <InvoicePdfTab invoice={currentInvoice} />}
      </Box>

      {isPayOpen && (
        <PayInvoiceModal
          open={isPayOpen}
          onClose={() => setIsPayOpen(false)}
          invoice={currentInvoice}
        />
      )}
    </Box>
  );
};
export default InvoicePage;
